/**
 * Hook de Monitoramento da Dead Letter Queue (DLQ)
 * 
 * Fornece funções para buscar estatísticas, listar itens,
 * reprocessar e descartar mensagens da DLQ do Flow Engine 3.0.
 * 
 * @author Sistema de Flows v3.0 - DLQ
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

/**
 * Interface para estatísticas da DLQ
 */
export interface DlqStats {
  total: number;
  byReason: Record<string, number>;
  oldestAt: string | null;
  newestAt: string | null;
}

/**
 * Interface para item da DLQ
 */
export interface DlqItem {
  msg_id: number;
  enqueued_at: string;
  message: Record<string, unknown>;
  dlq_reason: string;
  dlq_error: string | null;
  dlq_moved_at: string;
  original_msg_id: number | null;
  tenant_id: string | null;
  flow_id: string | null;
  conversation_id: string | null;
  contact_id: string | null;
  provider_slug: string | null;
  action: string | null;
  // Campos enriquecidos
  tenant_name?: string;
  flow_name?: string;
  contact_phone?: string;
}

/**
 * Interface de resposta da RPC get_dlq_flows_stats
 */
interface DlqStatsRpcResponse {
  total_count: number;
  by_reason: Record<string, number>;
  oldest_message_at: string | null;
  newest_message_at: string | null;
}

/**
 * Interface de resposta da RPC get_dlq_flows_items
 */
interface DlqItemRpcResponse {
  msg_id: number;
  enqueued_at: string;
  message: Record<string, unknown>;
  dlq_reason: string;
  dlq_error: string | null;
  dlq_moved_at: string;
  original_msg_id: number | null;
  tenant_id: string | null;
  flow_id: string | null;
  conversation_id: string | null;
  contact_id: string | null;
  provider_slug: string | null;
  action: string | null;
}

/**
 * Hook para buscar estatísticas da DLQ
 * Usa RPC get_dlq_flows_stats
 */
export function useDlqStats() {
  return useQuery({
    queryKey: ['dlq-stats'],
    queryFn: async (): Promise<DlqStats> => {
      const { data, error } = await supabase.rpc('get_dlq_flows_stats');
      
      if (error) {
        logger.error('[useDlqStats] Erro ao buscar estatísticas:', error.message);
        throw error;
      }
      
      // RPC retorna array com um elemento
      const stats = (data as unknown as DlqStatsRpcResponse[])?.[0];
      
      if (!stats) {
        return {
          total: 0,
          byReason: {},
          oldestAt: null,
          newestAt: null
        };
      }
      
      return {
        total: stats.total_count || 0,
        byReason: stats.by_reason || {},
        oldestAt: stats.oldest_message_at,
        newestAt: stats.newest_message_at
      };
    },
    refetchInterval: 30000, // Auto-refresh a cada 30 segundos
  });
}

/**
 * Hook para buscar contagem da DLQ (para badge no menu)
 */
export function useDlqCount() {
  return useQuery({
    queryKey: ['dlq-count'],
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc('get_dlq_flows_count');
      
      if (error) {
        logger.error('[useDlqCount] Erro ao buscar contagem:', error.message);
        throw error;
      }
      
      return (data as number) || 0;
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook para buscar itens da DLQ com paginação e filtro
 */
export function useDlqItems(
  limit: number = 50,
  offset: number = 0,
  reasonFilter: string | null = null
) {
  return useQuery({
    queryKey: ['dlq-items', limit, offset, reasonFilter],
    queryFn: async (): Promise<DlqItem[]> => {
      const { data, error } = await supabase.rpc('get_dlq_flows_items', {
        p_limit: limit,
        p_offset: offset,
        p_reason: reasonFilter
      });
      
      if (error) {
        logger.error('[useDlqItems] Erro ao buscar itens:', error.message);
        throw error;
      }
      
      if (!data || data.length === 0) return [];
      
      const rawItems = data as unknown as DlqItemRpcResponse[];
      
      // Extrair IDs únicos para enriquecimento
      const tenantIds = new Set<string>();
      const flowIds = new Set<string>();
      const contactIds = new Set<string>();
      
      rawItems.forEach(item => {
        if (item.tenant_id) tenantIds.add(item.tenant_id);
        if (item.flow_id) flowIds.add(item.flow_id);
        if (item.contact_id) contactIds.add(item.contact_id);
      });
      
      // Buscar dados enriquecidos em paralelo
      const [tenantsResult, flowsResult, contactsResult] = await Promise.all([
        tenantIds.size > 0
          ? supabase.from('tenants').select('id, name').in('id', Array.from(tenantIds))
          : Promise.resolve({ data: [] }),
        flowIds.size > 0
          ? supabase.from('flows').select('id, name').in('id', Array.from(flowIds))
          : Promise.resolve({ data: [] }),
        contactIds.size > 0
          ? supabase.from('contacts').select('id, phone').in('id', Array.from(contactIds))
          : Promise.resolve({ data: [] }),
      ]);
      
      // Criar Maps para lookup O(1)
      const tenantMap = new Map<string, string>(
        (tenantsResult.data || []).map((t: { id: string; name: string }) => [t.id, t.name])
      );
      const flowMap = new Map<string, string>(
        (flowsResult.data || []).map((f: { id: string; name: string }) => [f.id, f.name])
      );
      const contactMap = new Map<string, string>(
        (contactsResult.data || []).map((c: { id: string; phone: string | null }) => [c.id, c.phone || ''])
      );
      
      // Enriquecer itens
      return rawItems.map(item => ({
        ...item,
        tenant_name: item.tenant_id ? tenantMap.get(item.tenant_id) || 'N/A' : 'N/A',
        flow_name: item.flow_id ? flowMap.get(item.flow_id) || 'N/A' : 'N/A',
        contact_phone: item.contact_id ? contactMap.get(item.contact_id) || '' : '',
      }));
    },
    refetchInterval: 30000,
  });
}

/**
 * Hook para reprocessar um item da DLQ
 */
export function useReprocessDlqItem() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (msgId: number): Promise<boolean> => {
      const { data, error } = await supabase.rpc('reprocess_dlq_flows_item', {
        p_msg_id: msgId
      });
      
      if (error) {
        logger.error('[useReprocessDlqItem] Erro ao reprocessar:', error.message);
        throw error;
      }
      
      return data as boolean;
    },
    onSuccess: () => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['dlq-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-items'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-count'] });
      queryClient.invalidateQueries({ queryKey: ['flow-queue-stats-pgmq'] });
    }
  });
}

/**
 * Hook para descartar um item da DLQ
 */
export function useDiscardDlqItem() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (msgId: number): Promise<boolean> => {
      const { data, error } = await supabase.rpc('discard_dlq_flows_item', {
        p_msg_id: msgId
      });
      
      if (error) {
        logger.error('[useDiscardDlqItem] Erro ao descartar:', error.message);
        throw error;
      }
      
      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dlq-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-items'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-count'] });
    }
  });
}

/**
 * Hook para reprocessar múltiplos itens em lote
 */
export function useReprocessDlqBatch() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (msgIds: number[]): Promise<number> => {
      const { data, error } = await supabase.rpc('reprocess_dlq_flows_batch', {
        p_msg_ids: msgIds
      });
      
      if (error) {
        logger.error('[useReprocessDlqBatch] Erro ao reprocessar lote:', error.message);
        throw error;
      }
      
      return (data as number) || 0;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dlq-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-items'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-count'] });
      queryClient.invalidateQueries({ queryKey: ['flow-queue-stats-pgmq'] });
    }
  });
}

/**
 * Hook para descartar itens antigos da DLQ
 */
export function useDiscardOldDlqItems() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (days: number = 7): Promise<number> => {
      const { data, error } = await supabase.rpc('discard_old_dlq_flows_items', {
        p_days: days
      });
      
      if (error) {
        logger.error('[useDiscardOldDlqItems] Erro ao descartar antigos:', error.message);
        throw error;
      }
      
      return (data as number) || 0;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dlq-stats'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-items'] });
      queryClient.invalidateQueries({ queryKey: ['dlq-count'] });
    }
  });
}
